/**
 * Add-to-cart animation.
 *
 * Ported from the Horizon theme's `fly-to-cart.js`, which is an ES module
 * built on Horizon's `Component` base class and `@theme/utilities`. Dawn
 * loads plain deferred scripts with no import map, so this version drops
 * both dependencies and extends HTMLElement directly.
 *
 * Public entry point:
 *
 *   window.flyToCart(button, { variant, image })
 *
 * `variant` picks the easing set ('main' | 'quick' | 'sticky'); `image`
 * overrides the auto-resolved product image. Callers pass the add-to-cart
 * button and everything else is derived from the DOM.
 *
 * Styles live in fly-to-cart.css.
 */

(function () {
  'use strict';

  // Dawn's header cart link. Falls back to the generic icon class in case
  // a section renders the header without the id.
  var CART_ICON_SELECTOR = '#cart-icon-bubble, .header__icon--cart';

  // Inlined rather than fetched from an .svg asset so the burst is ready
  // on the first click with no network round-trip.
  var BURST_SVG = [
    '<svg aria-hidden="true" class="checkmark-burst" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">',
    '<g class="check">',
    '<circle class="ring" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
    '<path class="tick" d="M9 12.75L11.25 15L15 9.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>',
    '</g>',
    '<g class="burst">',
  ]
    .join('')
    .concat(
      Array.from({ length: 8 }, function (_, index) {
        return (
          '<g style="--index: ' +
          index +
          ';"><line class="line" stroke-linecap="round" pathLength="1" x1="12" y1="8.5" x2="12" y2="15.5" stroke="currentColor"/></g>'
        );
      }).join('')
    )
    .concat('</g></svg>');

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * The flying element itself. Horizon measured both endpoints with an
   * IntersectionObserver so the rects come from the observer's own
   * bookkeeping instead of forcing a synchronous layout on click; that
   * trick is kept here.
   */
  class FlyToCartComponent extends HTMLElement {
    connectedCallback() {
      if (!this.source || !this.destination) {
        this.remove();
        return;
      }

      var self = this;
      var observer = new IntersectionObserver(function (entries) {
        var sourceRect = null;
        var destinationRect = null;

        entries.forEach(function (entry) {
          if (entry.target === self.source) {
            sourceRect = entry.boundingClientRect;
          } else if (entry.target === self.destination) {
            destinationRect = entry.boundingClientRect;
          }
        });

        observer.disconnect();

        if (sourceRect && destinationRect) {
          self.animateTo(sourceRect, destinationRect);
        } else {
          // One endpoint scrolled out of the observer's reach; nothing
          // sensible to animate between, so clean up rather than leave a
          // stray fixed-position node in the page.
          self.remove();
        }
      });

      observer.observe(this.source);
      observer.observe(this.destination);
    }

    /**
     * Positions the element at the source and hands off to CSS, which
     * runs the three composited keyframes.
     *
     * @param {DOMRectReadOnly} sourceRect
     * @param {DOMRectReadOnly} destinationRect
     */
    animateTo(sourceRect, destinationRect) {
      var startX = sourceRect.left + sourceRect.width / 2;
      var startY = sourceRect.top + sourceRect.height / 2;
      var endX = destinationRect.left + destinationRect.width / 2;
      var endY = destinationRect.top + destinationRect.height / 2;

      if (this.useSourceSize) {
        this.style.setProperty('--width', sourceRect.width + 'px');
        this.style.setProperty('--height', sourceRect.height + 'px');
      }

      this.style.setProperty('--start-x', startX + 'px');
      this.style.setProperty('--start-y', startY + 'px');
      this.style.setProperty('--travel-x', endX - startX + 'px');
      this.style.setProperty('--travel-y', endY - startY + 'px');

      var self = this;

      // Let the custom properties land before we wait on the animations,
      // otherwise getAnimations() can resolve against the pre-positioned
      // state and the element is removed a frame too early.
      requestAnimationFrame(function () {
        var animations = self.getAnimations();

        if (!animations.length) {
          self.remove();
          return;
        }

        Promise.allSettled(
          animations.map(function (animation) {
            return animation.finished;
          })
        ).then(function () {
          self.remove();
        });
      });
    }
  }

  if (!customElements.get('fly-to-cart')) {
    customElements.define('fly-to-cart', FlyToCartComponent);
  }

  /**
   * Finds the product image to fling, searching outward from the button.
   * Dawn's markup differs between the PDP gallery, product cards, and the
   * custom featured-product section, so each is tried in turn.
   *
   * @param {Element} button
   * @returns {string|null}
   */
  function resolveImage(button) {
    var explicit = button.dataset.productVariantMedia;
    if (explicit) return explicit;

    var scope =
      button.closest('.mc-product') ||
      button.closest('.mc-feature') ||
      button.closest('product-info') ||
      button.closest('.product') ||
      button.closest('.card-wrapper') ||
      button.closest('.card') ||
      button.closest('quick-add-modal') ||
      // The PDP sticky bar sits outside .mc-product but inside the section.
      button.closest('.shopify-section') ||
      document;

    var selectors = [
      // Custom mc-gallery (PDP and featured product). The visible slide is
      // flagged with .is-active, not [hidden], so match on the class.
      '[data-mc-slide].is-active img',
      '.mc-gallery__slide.is-active img',
      '[data-mc-slide] img',
      // Dawn's stock gallery, active slide first.
      '.product__media-item.is-active img',
      '.product__media img',
      // Product cards / quick add.
      '.card__media img',
      '.media img',
      'img',
    ];

    for (var i = 0; i < selectors.length; i++) {
      var img = scope.querySelector(selectors[i]);
      if (img && (img.currentSrc || img.src)) return img.currentSrc || img.src;
    }

    return null;
  }

  /**
   * Injects the checkmark-burst markup into a Dawn add-to-cart button and
   * tags the existing label so the CSS can wipe it out. Idempotent.
   *
   * @param {HTMLElement} button
   */
  function prepareButton(button) {
    button.classList.add('add-to-cart-button');

    if (button.querySelector('.add-to-cart__added')) return;

    // Dawn wraps the label in a bare <span>; the spinner is a sibling.
    var label = button.querySelector('span:not(.add-to-cart__added):not(.visually-hidden)');
    if (label) {
      label.classList.add('add-to-cart-text');
    } else if (button.childNodes.length) {
      // The custom featured-product button holds a raw text node. Wrap it
      // so it can be animated independently of the burst.
      var wrapper = document.createElement('span');
      wrapper.className = 'add-to-cart-text';
      while (button.firstChild) wrapper.appendChild(button.firstChild);
      button.appendChild(wrapper);
    }

    var added = document.createElement('span');
    added.className = 'add-to-cart__added';
    added.innerHTML = '<span class="svg-wrapper add-to-cart__added-icon">' + BURST_SVG + '</span>';
    button.appendChild(added);
  }

  /**
   * Plays the add-to-cart animation: the button burst, plus the image
   * arcing to the cart icon.
   *
   * Safe to call unconditionally — it no-ops when the cart icon is absent
   * (e.g. a header variant without one) or motion is reduced.
   *
   * @param {HTMLElement} button - the add-to-cart button that was clicked
   * @param {{variant?: string, image?: string}} [options]
   */
  function flyToCart(button, options) {
    if (!button) return;

    options = options || {};

    prepareButton(button);

    // The burst reads as feedback, not decoration, so it still runs for
    // reduced-motion users — the CSS drops the staged transitions there.
    button.dataset.added = 'true';
    clearTimeout(button.flyToCartResetTimeout);
    button.flyToCartResetTimeout = setTimeout(function () {
      delete button.dataset.added;
    }, 2000);

    if (prefersReducedMotion()) return;

    var cartIcon = document.querySelector(CART_ICON_SELECTOR);
    if (!cartIcon) return;

    var image = options.image || resolveImage(button);
    if (!image) return;

    var element = document.createElement('fly-to-cart');
    var variant = options.variant;

    if (!variant) {
      variant = button.classList.contains('quick-add__submit') || button.closest('.card-wrapper') ? 'quick' : 'main';
    }

    element.classList.add('fly-to-cart--' + variant);
    element.style.setProperty('background-image', 'url(' + image + ')');
    element.style.setProperty('--start-opacity', '0');
    element.source = button;
    element.destination = cartIcon;

    document.body.appendChild(element);
  }

  window.flyToCart = flyToCart;
})();
